import { FilterOption, filter } from '@zspace/data';
import {
  PaginatedContentConfig,
  SalesOrderDevicesTableRow,
} from '@zspace/types';
import { useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import InboxTable from '../../ui/inbox-table/inbox-table';
import InfiniteTable from '../../ui/table/infinite-table/infinite-table';
import { Column } from '../../ui/table/types';

const columns: Column<SalesOrderDevicesTableRow>[] = [
  {
    key: 'model',
    className: 'is-width-80',
    render: (el) => el.model,
    title: 'Hardware model',
    sortable: false,
  },
  {
    key: 'amount',
    className: 'is-width-20',
    render: (el) => el.amount,
    title: 'Amount',
    sortable: false,
  },
];

const filterOptions: FilterOption<SalesOrderDevicesTableRow>[] = [
  {
    getter: (el) => el.model,
    searchable: true,
  },
  {
    getter: (el) => String(el.amount),
    searchable: false,
  },
];

export interface SalesOrderDevicesTableProps {
  data: SalesOrderDevicesTableRow[];
}

const ITEMS_PER_PAGE = 9;

export function SalesOrderDevicesTable({ data }: SalesOrderDevicesTableProps) {
  const [tableData, setTableData] = useState<SalesOrderDevicesTableRow[]>(
    data.slice(0, ITEMS_PER_PAGE)
  );

  const [tableConfig, setTableConfig] = useState<PaginatedContentConfig>({
    itemsPerPage: ITEMS_PER_PAGE,
    pageNumber: 1,
    pages: Math.ceil(data.length / ITEMS_PER_PAGE),
    search: '',
    sortBy: '',
  });

  const emptyContent = useMemo(() => {
    const hasAppliedFilters = tableConfig.search !== '';
    let emptyContentText = 'This sales order does not contain any devices';
    if (hasAppliedFilters) {
      emptyContentText =
        'There are no devices that match the selected criteria';
    }
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span className="my-2 has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [tableConfig.search]);

  const fetchMoreDevices = useCallback(async () => {
    const newTableConfig = {
      ...tableConfig,
      pageNumber: tableConfig.pageNumber + 1,
    };
    const filteredData = filter(data, newTableConfig, filterOptions);
    const newTableData = filteredData.slice(
      (newTableConfig.pageNumber - 1) * newTableConfig.itemsPerPage,
      newTableConfig.pageNumber * newTableConfig.itemsPerPage
    );

    newTableConfig.pages = Math.ceil(
      filteredData.length / newTableConfig.itemsPerPage
    );

    setTableData((prevTableData) => prevTableData.concat(newTableData));
    setTableConfig(newTableConfig);
  }, [data, tableConfig]);

  return (
    <InboxTable
      table={
        <InfiniteTable
          dataSource={tableData}
          columns={columns}
          fetchMore={fetchMoreDevices}
          hasMore={tableData.length < data.length}
          config={tableConfig}
          rowKey={'model'}
          empty={emptyContent}
        />
      }
      config={tableConfig}
      enableSearch={false}
    />
  );
}

export default SalesOrderDevicesTable;
